<template>
  <div class="settings-view">
    <PageHeader title="Users" :subtitle="`${users?.totalCount ?? 0} members · select a member to manage access and status.`" />
    <div v-if="message" class="workspace-alert">{{ message }}</div>
    <AsyncState :loading="!users || (canManageRoles && !roles)">
      <div class="access-card">
        <div class="access-tools">
          <div class="settings-search">
            <Search class="icon" />
            <input v-model="search" type="search" placeholder="Search by name or email on this page" />
          </div>
          <span class="access-count">{{ users?.totalCount ?? 0 }} members</span>
        </div>
        <div class="admin-table-wrap access-table-wrap">
        <table class="admin-table user-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Email status</th>
              <th>Account status</th>
              <th v-for="role in roleColumns" :key="role.id">{{ role.name }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in filteredUsers" :key="user.id">
              <td>
                <RouterLink class="app-link user-cell" :to="`/settings/users/${user.id}`">
                  <UserAvatar :name="user.name" size="medium" aria-hidden="true" />
                  <strong>{{ user.name }}</strong>
                </RouterLink>
              </td>
              <td>
                <RouterLink class="app-link" :to="`/settings/users/${user.id}`">{{ user.email }}</RouterLink>
              </td>
              <td><span :class="emailStatusClass(user)">{{ user.emailVerified ? 'Verified' : 'Unverified' }}</span></td>
              <td><span :class="accountStatusClass(user)">{{ user.lockedOut ? 'Banned' : 'Active' }}</span></td>
              <td v-for="role in roleColumns" :key="role.id">
                <Check
                  v-if="(user.globalRoleNames ?? []).includes(role.name)"
                  class="icon role-check"
                  :aria-label="`Has ${role.name}`"
                />
                <span v-else class="role-empty" aria-hidden="true">—</span>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="filteredUsers.length === 0" class="settings-state">
          <SearchX class="icon" />
          <span>No users match “{{ search }}”.</span>
        </div>
      </div>
        <div class="pagination-row">
          <button type="button" class="btn" :disabled="page <= 1" @click="previousPage">Previous</button>
          <span>Page {{ page }} of {{ totalPages }}</span>
          <button type="button" class="btn" :disabled="page >= totalPages" @click="nextPage">Next</button>
        </div>
      </div>
    </AsyncState>
  </div>
</template>

<script setup lang="ts">
import { Check, Search, SearchX } from 'lucide-vue-next';
import { computed, onMounted, ref } from 'vue';
import { RouterLink } from 'vue-router';
import {
  PermissionKind,
  QueriesService,
  RoleScope,
  type GetRolesQueryResponse,
  type GetUsersQueryResponse,
  type UserSummary
} from '../api/generated';
import AsyncState from '../components/settings/AsyncState.vue';
import PageHeader from '../components/settings/PageHeader.vue';
import UserAvatar from '../components/UserAvatar.vue';
import { getErrorMessage, useSessionStore } from '../stores/session';

const pageSize = 20;
const session = useSessionStore();
const users = ref<GetUsersQueryResponse | null>(null);
const roles = ref<GetRolesQueryResponse | null>(null);
const page = ref(1);
const search = ref('');
const message = ref<string | null>(null);
const canManageRoles = computed(() => session.hasGlobalPermission(PermissionKind.MANAGE_ROLES));
const globalRoles = computed(() => roles.value?.roles.filter(role => role.scope === RoleScope.GLOBAL) ?? []);
const roleColumns = computed(() => canManageRoles.value ? globalRoles.value : []);
const totalPages = computed(() => Math.max(1, Math.ceil(Number(users.value?.totalCount ?? 0) / pageSize)));
const filteredUsers = computed(() => {
  const term = search.value.trim().toLowerCase();
  const all = users.value?.users ?? [];
  if (!term) {
    return all;
  }

  return all.filter(user => `${user.name} ${user.email}`.toLowerCase().includes(term));
});

onMounted(async () => {
  await loadInitial();
});

async function loadInitial(): Promise<void> {
  try {
    users.value = session.pageBootstrap?.users ?? null;
    roles.value = session.pageBootstrap?.roles ?? null;
    if (users.value == null || (canManageRoles.value && roles.value == null)) {
      throw new Error('Users bootstrap payload was missing.');
    }

    message.value = null;
  } catch (error) {
    message.value = getErrorMessage(error, 'Unable to load users.');
  }
}

async function loadUsers(): Promise<void> {
  try {
    users.value = await QueriesService.queryGetUsersQuery({ requestBody: { page: page.value, pageSize } });
    message.value = null;
  } catch (error) {
    message.value = getErrorMessage(error, 'Unable to load users.');
  }
}

async function previousPage(): Promise<void> {
  if (page.value <= 1) {
    return;
  }

  page.value--;
  await loadUsers();
}

async function nextPage(): Promise<void> {
  if (page.value >= totalPages.value) {
    return;
  }

  page.value++;
  await loadUsers();
}

function emailStatusClass(user: UserSummary): string {
  return user.emailVerified ? 'status-pill success' : 'status-pill warning';
}

function accountStatusClass(user: UserSummary): string {
  return user.lockedOut ? 'status-pill danger' : 'status-pill success';
}

</script>
